import { formatMoney, toMinorUnits } from "@vouch/shared";
import { ArrowLeft, Check, Clock3, Plus, RotateCcw, Users, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FinancialTimeline } from "../components/Timeline";
import { Button, Card, Input, Select, Spinner } from "../components/ui";
import { useAuth } from "../lib/auth";
import {
  useAccounts,
  useCancelRepayment,
  useLoan,
  useRepayLoan,
  useRespondRepayment,
  useTimeline,
} from "../lib/hooks";
import { confirm } from "../lib/confirm";
import { toast } from "../lib/toast";

export function LoanDetailPage(): JSX.Element {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const { data, isLoading } = useLoan(id);
  const { data: timeline } = useTimeline("loan", id);
  const { data: accounts = [] } = useAccounts();
  const repay = useRepayLoan();
  const respond = useRespondRepayment();
  const cancelRepayment = useCancelRepayment();
  const [amount, setAmount] = useState("");
  const [accountId, setAccountId] = useState("");
  const [paidAt, setPaidAt] = useState(() => toDateTimeLocal(new Date()));

  const compatibleAccounts = useMemo(
    () => accounts.filter((account) => !data || account.currency === data.loan.currency),
    [accounts, data],
  );
  const selectedAccount = accountId || compatibleAccounts[0]?.id || accounts[0]?.id || "";

  if (isLoading || !data) return <Spinner />;
  const { loan, repayments, witnesses } = data;
  const isLender = loan.lenderUserId === user?.id;
  const pending = repayments.filter((row) => row.status === "pending");
  const confirmed = repayments.filter((row) => row.status === "accepted");
  const confirmedTotal = confirmed.reduce((sum, row) => sum + row.amount, 0);
  const pendingTotal = pending.reduce((sum, row) => sum + row.amount, 0);
  const unconfirmedOutstanding = Math.max(0, loan.outstanding - pendingTotal);

  const submitRepayment = async (): Promise<void> => {
    const amountMinor = toMinorUnits(Number(amount));
    if (!selectedAccount || amountMinor <= 0) return;
    try {
      await repay.mutateAsync({
        id: loan.id,
        accountId: selectedAccount,
        amountMinor,
        paidAt: new Date(paidAt).toISOString(),
      });
      setAmount("");
      setPaidAt(toDateTimeLocal(new Date()));
      toast.success("Repayment recorded and sent for confirmation");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not record repayment");
    }
  };

  const undoRepayment = async (repaymentId: string): Promise<void> => {
    const ok = await confirm({
      title: "Undo this unconfirmed repayment?",
      message: "This one-sided record and its pending notification will be permanently deleted.",
      confirmLabel: "Undo repayment",
      danger: true,
    });
    if (!ok) return;
    await cancelRepayment
      .mutateAsync(repaymentId)
      .then(() => toast.success("Unconfirmed repayment undone"))
      .catch((error) => toast.error(error instanceof Error ? error.message : "Could not undo repayment"));
  };

  const answer = async (repaymentId: string, accept: boolean): Promise<void> => {
    try {
      await respond.mutateAsync({ repaymentId, accept, accountId: accept ? selectedAccount : undefined });
      toast.success(accept ? "Installment confirmed" : "Installment declined");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update installment");
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 sm:space-y-6">
      <div>
        <Link to="/loans" className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-slate-500 hover:text-brand-500">
          <ArrowLeft size={16} /> Back to loans
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Loan detail</p>
            <h1 className="mt-1 break-words text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {loan.description || `Loan with ${isLender ? loan.borrower.name : loan.lender.name}`}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {loan.lender.name} lent to {loan.borrower.name} · started {new Date(loan.startDate).toLocaleDateString()}
            </p>
          </div>
          <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold capitalize text-blue-700">
            {loan.status.replaceAll("_", " ")}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
        <Metric label="Principal" value={formatMoney(loan.principal, loan.currency)} />
        <Metric label="Confirmed repaid" value={formatMoney(confirmedTotal, loan.currency)} tone="positive" />
        <Metric label="Awaiting confirmation" value={formatMoney(pendingTotal, loan.currency)} tone="warning" />
        <Metric label="Unconfirmed outstanding" value={formatMoney(unconfirmedOutstanding, loan.currency)} tone="warning" />
        <Metric className="col-span-2 xl:col-span-1" label="Confirmed outstanding" value={formatMoney(loan.outstanding, loan.currency)} tone="strong" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]">
        <Card>
          <div className="flex items-center gap-2">
            <Users size={18} className="text-blue-600" />
            <h2 className="font-semibold text-slate-800">Parties and witnesses</h2>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Party label="Lender" name={loan.lender.name} username={loan.lender.username} active={isLender} />
            <Party label="Borrower" name={loan.borrower.name} username={loan.borrower.username} active={!isLender} />
          </div>
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Witnesses</p>
            {witnesses.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No witnesses were added to this loan.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {witnesses.map((witness) => (
                  <li key={witness.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-sm text-slate-700">{witness.person?.name ?? witness.invitedEmail ?? "Invited witness"}</span>
                    <span className="text-xs font-semibold capitalize text-slate-500">{witness.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {(loan.status === "active" || loan.status === "overdue") && (
          <Card>
            <div className="flex items-center gap-2">
              <Plus size={18} className="text-blue-600" />
              <h2 className="font-semibold text-slate-800">Record a repayment</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Record money that was paid back, even while earlier repayments await confirmation.
            </p>
            <div className="mt-4 space-y-3">
              <Input label={`Amount (${loan.currency})`} type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} />
              <Input label="Paid on" type="datetime-local" value={paidAt} onChange={(event) => setPaidAt(event.target.value)} />
              <Select label={isLender ? "Receive into" : "Pay from"} value={selectedAccount} onChange={(event) => setAccountId(event.target.value)}>
                {(compatibleAccounts.length ? compatibleAccounts : accounts).map((account) => (
                  <option key={account.id} value={account.id}>{account.name} · {account.currency}</option>
                ))}
              </Select>
              <Button className="w-full" disabled={repay.isPending || !amount || !paidAt || !selectedAccount} onClick={() => void submitRepayment()}>
                {repay.isPending ? "Recording…" : "Record repayment"}
              </Button>
            </div>
          </Card>
        )}
      </div>

      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-slate-800">Repayments</h2>
            <p className="text-sm text-slate-500">Confirmed and currently unconfirmed repayments appear here.</p>
          </div>
          {pending.length > 0 && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">{pending.length} awaiting</span>}
        </div>
        {repayments.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No repayments have been recorded yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100">
            {repayments.map((row) => {
              const awaitingMe = row.status === "pending" && row.initiatorUserId !== user?.id;
              const canUndo = row.status === "pending" && row.initiatorUserId === user?.id;
              return (
                <li key={row.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{formatMoney(row.amount, loan.currency)}</p>
                    <p className="break-words text-xs leading-5 text-slate-500">
                      Paid {new Date(row.paidAt).toLocaleString()} · {row.initiatorUserId === user?.id ? "recorded by you" : "recorded by the other party"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    {row.status === "pending" ? <Clock3 size={16} className="text-amber-500" /> : row.status === "accepted" ? <Check size={16} className="text-emerald-600" /> : <X size={16} className="text-rose-500" />}
                    <span className="text-sm font-medium capitalize text-slate-600">{row.status}</span>
                    {awaitingMe && (
                      <>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" disabled={respond.isPending || !selectedAccount} onClick={() => void answer(row.id, true)}>Confirm</Button>
                        <Button className="min-h-10 flex-1 sm:flex-none" variant="ghost" disabled={respond.isPending} onClick={() => void answer(row.id, false)}>Decline</Button>
                      </>
                    )}
                    {canUndo && (
                      <Button variant="ghost" className="min-h-10 flex-1 text-rose-600 hover:text-rose-700 sm:flex-none" disabled={cancelRepayment.isPending} onClick={() => void undoRepayment(row.id)}>
                        <RotateCcw size={15} /> Undo
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {timeline && <FinancialTimeline data={timeline} />}
    </div>
  );
}

function toDateTimeLocal(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function Metric({ label, value, tone = "default", className = "" }: { label: string; value: string; tone?: "default" | "positive" | "warning" | "strong"; className?: string }): JSX.Element {
  const color = tone === "positive" ? "text-emerald-600" : tone === "warning" ? "text-amber-600" : tone === "strong" ? "text-blue-700" : "text-slate-800";
  return <Card className={`min-w-0 p-3 sm:p-4 ${className}`}><p className="text-[11px] font-semibold uppercase leading-4 tracking-wide text-slate-400 sm:text-xs">{label}</p><p className={`mt-1 break-words text-lg font-bold sm:text-xl ${color}`}>{value}</p></Card>;
}

function Party({ label, name, username, active }: { label: string; name: string; username: string | null; active: boolean }): JSX.Element {
  return <div className="rounded-xl border border-slate-200 p-3"><div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>{active && <span className="text-[10px] font-bold uppercase text-blue-600">You</span>}</div><p className="mt-1 font-medium text-slate-800">{name}</p>{username && <p className="text-xs text-slate-500">@{username}</p>}</div>;
}
